import { AnchorHTMLAttributes, ButtonHTMLAttributes, HTMLAttributes, PropsWithChildren } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLightbulb, faCircleInfo } from "@fortawesome/sharp-regular-svg-icons";
import classNames from "classnames";

import styles from "./tip.module.scss";

export type TipProps = {
  variant?: "tip" | "callout";
};

type TipHeadingProps = Omit<HTMLAttributes<HTMLSpanElement>, "className">;

const TipHeading = ({ children, ...rest }: TipHeadingProps) => (
  <span className={styles.heading} {...rest}>
    {children}
  </span>
);

type TipCaptionProps = Omit<HTMLAttributes<HTMLSpanElement>, "className">;

const TipCaption = ({ children, ...rest }: TipCaptionProps) => (
  <span className={styles.caption} {...rest}>
    {children}
  </span>
);

export type TipLinkAsAnchorProps = {
  as?: "a";
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "className">;

export type TipLinkAsButtonProps = {
  as: "button";
  onClick: () => void;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "type" | "onClick">;

export type TipLinkProps = TipLinkAsAnchorProps | TipLinkAsButtonProps;

const TipLink = (props: TipLinkProps) => {
  if (props.as === "button") {
    const { as: _as, children, ...rest } = props;
    return (
      <button type="button" className={styles.link} {...rest}>
        {children}
      </button>
    );
  }

  const { as: _as, children, target = "_blank", rel, ...rest } = props;
  return (
    <a className={styles.link} target={target} rel={target === "_blank" ? "noreferrer" : rel} {...rest}>
      {children}
    </a>
  );
};

const TipBase = ({ children, variant = "tip" }: PropsWithChildren<TipProps>) => (
  <div
    className={classNames(styles.container, {
      [styles.variantTip]: variant === "tip",
      [styles.variantCallout]: variant === "callout",
    })}
  >
    <FontAwesomeIcon icon={variant === "tip" ? faLightbulb : faCircleInfo} className={styles.icon} />
    <div className={styles.contentContainer}>{children}</div>
  </div>
);

export const Tip = Object.assign(TipBase, {
  Caption: TipCaption,
  Heading: TipHeading,
  Link: TipLink,
});
